'use client';

import { useEffect, useRef } from 'react';

import { sectionHeaderReveal } from '@/config/section-header-reveal';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { loadScrollSceneRuntime } from '@/motion/scrollScene';
import { appendSectionHeaderContent } from '@/motion/sectionHeaderContentReveal';

type SectionHeaderRevealProps = {
  children: React.ReactNode;
  className?: string;
  /**
   * When set, attach the scrub timeline only while this `matchMedia` query
   * matches. Omit for chapter headers that always scrub (and for Introduction,
   * which uses the hero runway instead).
   */
  enabledMedia?: string;
  /**
   * Optional document-wide selector for chrome that should fade with the
   * header (e.g. `HERO_REVEAL_SELECTOR` for StickyNav on narrow Intro). Queried
   * outside `root` because StickyNav sits outside `#introduction`.
   */
  chromeSelector?: string;
  /** Rise distance (px) for `chromeSelector` targets. Default 24. */
  chromeOffsetY?: number;
};

const DEFAULT_CHROME_OFFSET_Y = 24;
/** Relative weight so chrome fades across the same scrub window as the headline. */
const CHROME_DURATION = sectionHeaderReveal.headline.weight;

/**
 * Scroll-linked GSAP island for chapter headers (State / Pressure / Response).
 * Queries `[data-section-headline|aside|body-reveal]` inside this root and
 * scrubs SplitText → aside → body over `sectionHeaderReveal` start/end.
 * Bidirectional — scrolling back reverses the reveal. Introduction uses the
 * scrubbed hero runway instead.
 *
 * Optional `chromeSelector` fades document-level chrome in parallel with the
 * headline when needed.
 */
export function SectionHeaderReveal({
  children,
  className,
  enabledMedia,
  chromeSelector,
  chromeOffsetY = DEFAULT_CHROME_OFFSET_Y,
}: SectionHeaderRevealProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;

    const root = rootRef.current;
    if (!root) return;

    let cancelled = false;
    let sceneCleanup: (() => void) | null = null;
    let matchMediaInstance: { revert: () => void } | null = null;

    const attach = async () => {
      const runtime = await loadScrollSceneRuntime();
      if (cancelled || !rootRef.current) return;

      const { gsap, ScrollTrigger } = runtime;

      const run = () => {
        if (cancelled || !rootRef.current) return;

        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: rootRef.current,
            start: sectionHeaderReveal.timelineStart,
            end: sectionHeaderReveal.timelineEnd,
            scrub: true,
            invalidateOnRefresh: true,
          },
        });

        const chromeTargets = chromeSelector
          ? gsap.utils.toArray<HTMLElement>(chromeSelector)
          : [];

        if (chromeTargets.length > 0) {
          timeline.fromTo(
            chromeTargets,
            { autoAlpha: 0, y: chromeOffsetY },
            {
              autoAlpha: 1,
              y: 0,
              ease: 'none',
              duration: CHROME_DURATION,
            },
            0,
          );
        }

        const { hasTargets, cleanup: contentCleanup } = appendSectionHeaderContent(
          runtime,
          {
            root: rootRef.current,
            timeline,
            timing: sectionHeaderReveal,
            headlineAt: 0,
          },
        );

        if (!hasTargets && chromeTargets.length === 0) {
          contentCleanup();
          timeline.scrollTrigger?.kill();
          timeline.kill();
          return;
        }

        sceneCleanup = () => {
          contentCleanup();
          timeline.scrollTrigger?.kill();
          timeline.kill();
        };

        ScrollTrigger.refresh();
        // Late attach: catch up to current scroll so mid-page loads aren't stuck
        // at the CSS pre-hide / from state.
        const trigger = timeline.scrollTrigger;
        if (trigger) {
          timeline.progress(trigger.progress);
        }
      };

      const scheduleRun = () => {
        if (document.fonts?.status === 'loaded') {
          run();
        } else if (document.fonts?.ready) {
          void document.fonts.ready.then(() => {
            if (!cancelled) run();
          });
        } else {
          run();
        }
      };

      if (enabledMedia) {
        const matchMedia = gsap.matchMedia();
        matchMediaInstance = matchMedia;
        matchMedia.add(enabledMedia, () => {
          scheduleRun();
          return () => {
            sceneCleanup?.();
            sceneCleanup = null;
          };
        });
        return;
      }

      scheduleRun();
    };

    void attach();

    return () => {
      cancelled = true;
      matchMediaInstance?.revert();
      matchMediaInstance = null;
      sceneCleanup?.();
      sceneCleanup = null;
    };
  }, [prefersReducedMotion, enabledMedia, chromeSelector, chromeOffsetY]);

  return (
    <div ref={rootRef} className={className}>
      {children}
    </div>
  );
}
